import { openDB } from 'idb'
import { getDatabase, NoteRepository, readOrCreateBootstrapDeviceId } from './db'
import { activeWorkspace, workspaceDatabaseName, workspaceLockName, type WorkspaceScope } from './workspace'
import { hasDraftContent, noteFromDraft, type ActiveDraft, type Note } from '../domain/note'
import type { NoteAsset } from '../assets/assetTypes'
import { GUEST_SCOPE } from '../auth/planLimits'
import { manualContextKey, type ManualContext } from '../intelligence/contextLab/manualContexts'

const GUEST_CONTEXT_PREFIX = `manual-context:v1:${encodeURIComponent(GUEST_SCOPE)}:`

async function readGuest(scope: WorkspaceScope) {
  const name = workspaceDatabaseName(scope, null)
  let missing = false
  const db = await openDB(name, undefined, { upgrade(_db, version, _next, tx) {
    if (version === 0) { missing = true; void tx.done.catch(() => undefined); tx.abort() }
  } }).catch(error => { if (!missing) throw error; return null })
  if (!db) return null
  try {
    if (!db.objectStoreNames.contains('notes')) return null
    const tx = db.transaction(['notes', 'drafts', 'assets', 'meta'], 'readonly')
    if ((await tx.objectStore('meta').get('sync-owner'))?.value) throw new Error('Guest workspace unexpectedly belongs to an account. Its content has been preserved separately.')
    const [notes, draft, assets, contextRows] = await Promise.all([
      tx.objectStore('notes').getAll() as Promise<Note[]>,
      tx.objectStore('drafts').get('active') as Promise<ActiveDraft | undefined>,
      tx.objectStore('assets').getAll() as Promise<NoteAsset[]>,
      tx.objectStore('meta').getAll(IDBKeyRange.bound(GUEST_CONTEXT_PREFIX, GUEST_CONTEXT_PREFIX + '\uffff')),
    ])
    await tx.done
    // The Contexts made before signing in (the notes they link are in the same workspace).
    const contexts = contextRows.map(row => JSON.parse(String(row.value)) as ManualContext).filter(context => context?.version === 1 && Array.isArray(context.links))
    return { notes, draft, assets, contexts }
  } finally { db.close() }
}
export async function guestDecisionNeeded(scope: WorkspaceScope): Promise<boolean> {
  const owner = activeWorkspace()?.owner
  if (!owner) return false
  const guest = await readGuest(scope)
  if (!guest || (!guest.notes.length && !(guest.draft && hasDraftContent(guest.draft)) && !guest.assets.length)) return false
  return (await new NoteRepository().getMeta('guest-workspace-choice:v1')) === undefined
}

export async function decideGuestWorkspace(add: boolean): Promise<void> {
  const workspace = activeWorkspace()
  if (!workspace?.owner) throw new Error('Sign in before adding guest notes.')
  const owner = workspace.owner
  const repository = new NoteRepository()
  if (add) {
    if (!navigator.locks) throw new Error('Adding guest content safely requires Web Locks support. You can keep it separate.')
    await navigator.locks.request(workspaceLockName(workspaceDatabaseName(workspace.scope, null)), { mode: 'exclusive' }, async () => {
      const guest = await readGuest(workspace.scope)
      if (!guest) return
      const notes = new Map(guest.notes.map(note => [note.id, note]))
      if (guest.draft && hasDraftContent(guest.draft)) notes.set(guest.draft.noteId, noteFromDraft(guest.draft, notes.get(guest.draft.noteId) ?? null, readOrCreateBootstrapDeviceId()))
      for (const id of notes.keys()) {
        if (await repository.getNote(id)) throw new Error('Some guest notes already exist in this account. Keep the workspaces separate to avoid overwriting either copy.')
      }
      for (const asset of guest.assets) {
        if (await repository.getAsset(asset.id)) throw new Error('Some images already exist in this account. No content was overwritten.')
      }
      await repository.commitLocalNotes([...notes.values()].map(note => ({ note: { ...note, localSyncState: 'pending' as const } })), undefined, [], guest.assets)
      // Carry the guest's Contexts across under the account (an id that already exists there is left as it is).
      const database = await getDatabase()
      for (const context of guest.contexts) {
        const key = manualContextKey(owner, context.id)
        if (await database.get('meta', key)) continue
        await database.put('meta', { key, value: JSON.stringify({ ...context, ownerId: owner }) })
      }
    })
  }
  await repository.setMeta('guest-workspace-choice:v1', add ? 'added' : 'kept-separate')
}
